import moment, { Moment } from "moment";
import React, { useEffect, useState } from "react";
import { IAppointment, IAppointmentEvent, IDoctor, ITextInput } from "../../helpers/Interfaces";
import ApiService from "../../services/ApiService";
import DocumentButton from "../DocumentButton/DocumentButton";
import Input from "../Input/Input";
import InputDropDown from "../InputDropDown/InputDropDown";
import Modal from "../Modal/Modal";
import SelectTime from "../SelectTime/SelectTime";
import CheckBox from "../CheckBox/CheckBox";
import BlueHomeDot from "../../assets/images/BlueHomeDot.svg";
import BlueHomeDotDisable from "../../assets/images/BlueHomeDotDisable.svg";
import { IReferrer } from './../../helpers/Interfaces';

import "./AddAppointmentModal.scss";

type AddAppointmentModalProps = {
  readonly open: boolean;
  readonly closeModal: (modal: "add" | "edit" | "view", shouldUpdate: boolean) => void;
  readonly appointments: IAppointment[];
  readonly selectedDoctor: IDoctor | null;
}

type formDataType = {
  clinicId: string,
  patientName: string,
  date: Moment,
  startTime: Moment,
  endTime: Moment,
  notes: string,
  appointmentStatus: number,
  paperworkReceived: boolean,
  referrerId?: null | string,
  consultationMode?: null | number,
  invoiceStatus: number
  referrer: null | string,
}

function AddAppointmentModal(props: AddAppointmentModalProps) {
  const apiService = new ApiService();

  const [formData, setFormData] = useState<formDataType>({
    clinicId: "", patientName: "", date: moment(), startTime: moment(), endTime: moment(), notes: "", appointmentStatus: 0,
    paperworkReceived: false, referrerId: null, consultationMode: null, invoiceStatus: 0, referrer: null
  });
  const [formDataErrors, setFormDataErrors] = useState({
    clinicIdError: { showError: false, message: "" }, patientNameError: { showError: false, message: "" },
    startTimeError: { showError: false, message: "" }, endTimeError: { showError: false, message: "" }
  });
  const [loading, setLoading] = useState(false);
  const [clinics, setClinics] = useState([] as ITextInput[]);
  const [referrers, setReferrers] = useState([] as ITextInput[]);
  const [showAlert, setShowAlert] = useState({ isError: false, message: "", open: false });

  const consultationModeValues: ITextInput[] = [{ label: "Face To Face", value: 0 }, { label: "Telehealth", value: 1 }]
  const invoiceStatusValues: ITextInput[] = [
    { label: "Not Created", value: 0 },
    { label: "Invoice Created", value: 1 },
    { label: "Invoice Sent", value: 2 }
  ]

  useEffect(() => {
    getClinics();
    getReferrers();
  }, [props.selectedDoctor])

  function getClinics() {

    setLoading(true);

    let data = {
      doctorId: props.selectedDoctor ? props.selectedDoctor.doctorId : null
    }

    apiService.getClinicsByDoctorId(data).then((result: any) => {

      let data = result.data;

      if (!data?.isSuccessful) {
        let newShowAlert = showAlert;
        newShowAlert.isError = true;
        newShowAlert.message = data?.errorMessage;
        newShowAlert.open = true;
        setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        return;
      }

      let clinicsArr = [] as ITextInput[];

      data.clinics.forEach((clinic: { clinicName: string; id: string; }) => {
        let data = {
          label: clinic.clinicName,
          value: clinic.id
        }

        clinicsArr.push(data);
      });

      setClinics(clinicsArr);
    }).catch(() => {
      let newShowAlert = showAlert;
      newShowAlert.isError = true;
      newShowAlert.message = "Unexpected Error";
      newShowAlert.open = true;
      setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
    }).finally(() => {
      setLoading(false);
    });
  }

  function getReferrers() {

    setLoading(true);

    apiService.getReferrers().then((result) => {

      let data = result.data;

      if (!data?.isSuccessful) {
        let newShowAlert = showAlert;
        newShowAlert.isError = true;
        newShowAlert.message = data?.errorMessage;
        newShowAlert.open = true;
        setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        return;
      }

      let referrersArr = [] as ITextInput[];

      data?.referrers.forEach((referrer: IReferrer) => {
        let data = {
          label: referrer.name,
          value: referrer.id
        }

        referrersArr.push(data);
      });

      setReferrers(referrersArr);
    }).catch(() => {
      let newShowAlert = showAlert;
      newShowAlert.isError = true;
      newShowAlert.message = "Unexpected Error";
      newShowAlert.open = true;
      setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
    }).finally(() => {
      setLoading(false);
    });
  }

  function clearData() {
    let newFormData = formData;
    newFormData.clinicId = "";
    newFormData.patientName = "";
    newFormData.date = moment();
    newFormData.startTime = moment();
    newFormData.endTime = moment();
    setFormData((prevDetails: any) => ({ ...prevDetails, ...newFormData, }));

    let newFormDataErrors = formDataErrors;
    newFormDataErrors.clinicIdError.showError = false;
    newFormDataErrors.patientNameError.showError = false;
    newFormDataErrors.startTimeError.showError = false;
    newFormDataErrors.endTimeError.showError = false;
    setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));

    let newShowAlert = showAlert;
    newShowAlert.open = false;
    setShowAlert(prevDetails => ({ ...prevDetails, ...newShowAlert }));

    setLoading(false);
  }

  const handleClose = (shouldUpdate: boolean) => {
    clearData();
    props.closeModal("add", shouldUpdate);
  }

  const closeAlert = () => {
    let newShowAlert = showAlert;
    newShowAlert.open = false;
    setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
  }

  const initialConvertedData = props.appointments.map((appointment) => {
    const appointmentDate = moment(appointment.appointmentDate).toDate();

    const localEvents = appointment.events.map(event => {
      const localStartTime = moment(event.startTime).toDate();
      const localEndTime = moment(event.endTime).toDate();

      return {
        ...event,
        startTime: localStartTime,
        endTime: localEndTime,
      };
    });

    return {
      ...appointment,
      appointmentDate,
      events: localEvents,
    };
  });

  const convertedData = initialConvertedData.map((appointment, key, array) => {
    const localEvents: IAppointmentEvent[] = [];

    appointment.events.forEach(event => {
      const localStartTime = moment(event.startTime);
      const appointmentDateOnly = moment(appointment.appointmentDate).startOf('day');
      const startTimeDateOnly = localStartTime.startOf('day');

      if (appointmentDateOnly.isSame(startTimeDateOnly, 'day')) {
        localEvents.push(event);
      } else if (appointmentDateOnly.isBefore(startTimeDateOnly, 'day')) {
        if (key + 1 < array.length) {
          const nextAppointment = array[key + 1];
          nextAppointment.events = [...nextAppointment.events, event];
        }
      } else if (appointmentDateOnly.isAfter(startTimeDateOnly, 'day')) {
        if (key - 1 >= 0) {
          const prevAppointment = array[key - 1];
          prevAppointment.events = [...prevAppointment.events, event];
        }
      }
    });

    return {
      ...appointment,
      events: localEvents,
    };
  });

  const isInRange = (): boolean => {

    let currentDayAppointments: IAppointmentEvent[] = [];

    convertedData.filter((appointment) => {
      if (moment(appointment.appointmentDate).isSame(formData.date, "day")) {
        currentDayAppointments = appointment.events;
      }
    })

    let selectedAppointmentStartTime = moment(formData.startTime);
    let selectedAppointmentEndTime = moment(formData.endTime);

    for (let appointment of currentDayAppointments) {

      let currentAppointmentStartTime = moment(appointment.startTime);
      let currentAppointmentEndTime = moment(appointment.endTime);

      if (currentAppointmentStartTime.isBefore(selectedAppointmentEndTime) &&
        selectedAppointmentStartTime.isBefore(currentAppointmentEndTime)) {
        return true;
      };
    };

    return false;
  }

  function saveAppointment() {

    setLoading(true);

    if (formData.clinicId.trim() === "") {
      let newFormDataErrors = formDataErrors;
      newFormDataErrors.clinicIdError.showError = true;
      newFormDataErrors.clinicIdError.message = "REQUIRED";
      setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));
      setLoading(false);
      return;
    } else {
      let newFormDataErrors = formDataErrors;
      newFormDataErrors.clinicIdError.showError = false;
      setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));
    }

    if (formData.patientName.trim() === "" && formData.referrerId?.trim() === "") {
      let newShowAlert = showAlert;
      newShowAlert.isError = true;
      newShowAlert.message = "PLEASE ENTER A PATIENT NAME OR SELECT A REFERRER";
      newShowAlert.open = true;
      setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
      setLoading(false);
      return;
    }

    if (!(formData.startTime instanceof moment)) {
      let newFormDataErrors = formDataErrors;
      newFormDataErrors.startTimeError.showError = true;
      newFormDataErrors.startTimeError.message = "REQUIRED";
      setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));
      setLoading(false);
      return;
    } else {
      let newFormDataErrors = formDataErrors;
      newFormDataErrors.startTimeError.showError = false;
      setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));
    }

    if (!(formData.endTime instanceof moment)) {
      let newFormDataErrors = formDataErrors;
      newFormDataErrors.startTimeError.showError = true;
      newFormDataErrors.startTimeError.message = "REQUIRED";
      setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));
      setLoading(false);
      return;
    } else {
      let newFormDataErrors = formDataErrors;
      newFormDataErrors.startTimeError.showError = false;
      setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));
    }

    if (!(formData.endTime instanceof moment)) {
      let newFormDataErrors = formDataErrors;
      newFormDataErrors.endTimeError.showError = true;
      newFormDataErrors.endTimeError.message = "REQUIRED";
      setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));
      setLoading(false);
      return;
    } else {
      let newFormDataErrors = formDataErrors;
      newFormDataErrors.endTimeError.showError = false;
      setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));
    }

    if (!(moment(formData.startTime).isBefore(moment(formData.endTime)))) {
      let newFormDataErrors = formDataErrors;
      newFormDataErrors.endTimeError.showError = true;
      newFormDataErrors.endTimeError.message = "SELECT CORRECT TIME RANGE";
      setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));
      setLoading(false);
      return;
    } else {
      let newFormDataErrors = formDataErrors;
      newFormDataErrors.endTimeError.showError = false;
      setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));
    }

    if (isInRange()) {
      let newShowAlert = showAlert;
      newShowAlert.isError = true;
      newShowAlert.message = "Selected Time Slot is Unavailable";
      newShowAlert.open = true;
      setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
      setLoading(false);
      return;
    }

    const data = {
      clinicId: formData.clinicId,
      patientName: formData.patientName,
      appointmentStart: formData.startTime.format("YYYY-MM-DDTHH:mm:ss"),
      appointmentEnd: formData.endTime.format("YYYY-MM-DDTHH:mm:ss"),
      ...(props.selectedDoctor?.doctorId && { doctorId: props.selectedDoctor?.doctorId }),
      notes: formData.notes,
      appointmentStatus: formData.appointmentStatus,
      paperworkReceived: formData.paperworkReceived,
      referrerId: formData.referrerId,
      consultationMode: formData.consultationMode,
      invoiceStatus: formData.invoiceStatus,
      referrer: formData.referrer
    }

    apiService.addAppointment(data).then((result) => {

      let data = result.data;

      if (!data?.isSuccessful) {
        let newShowAlert = showAlert;
        newShowAlert.isError = true;
        newShowAlert.message = data?.errorMessage;
        newShowAlert.open = true;
        setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        return;
      }

      let newShowAlert = showAlert;
      newShowAlert.isError = false;
      newShowAlert.message = "Appointment Added";
      newShowAlert.open = true;
      setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));

      handleClose(true);
    }).catch(() => {
      let newShowAlert = showAlert;
      newShowAlert.isError = true;
      newShowAlert.message = "Unexpected Error";
      newShowAlert.open = true;
      setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
    }).finally(() => {
      setLoading(false);
    });
  }

  return (
    <Modal showAlert={showAlert} closeAlert={closeAlert} open={props.open} close={() => handleClose(false)} style={"add-appointment-card"}>
      <div className="add-appointment-card-container">
        <span className="appointment-card-title">Create Appointment</span>
        <div className="input-fields">
          <div className="input-field-details">
            <Input label={"PATIENT NAME"} value={formData.patientName} type={"search"}
              onChange={(value: any) => {
                let newFormData = formData;
                newFormData.patientName = value;
                setFormData((prevDetails) => ({ ...prevDetails, ...newFormData }));
              }} />
            <InputDropDown type="search" title={"CLINIC*"} values={clinics} typeable={false} showError={formDataErrors.clinicIdError.showError}
              onSelect={(value: any) => {
                if (value?.length > 0) {
                  let newFormData = formData;
                  newFormData.clinicId = value[0].value;
                  setFormData((prevDetails) => ({ ...prevDetails, ...newFormData }));
                }
              }} />
            <InputDropDown type="search" title={"REFERRER"} values={referrers} typeable
              onSelect={(val: any, asValue: any) => {
                let newFormData = formData;
                if (asValue) {
                  newFormData.referrer = val;
                  newFormData.referrerId = null;
                } else {
                  newFormData.referrerId = val[0].value;
                  newFormData.referrer = val[0].label;
                }
                setFormData(newFormData);
              }} />
          </div>
          <div className="input-field-date">
            <SelectTime formData={formData} setFormData={setFormData} />
          </div>
          <Input label='COMMENTS' value={formData.notes} type='textarea' rows={5}
            onChange={(value: string) => {
              setFormData((prevFormData) => ({ ...prevFormData, notes: value }));
            }} />
          <div className="optional-attributes">
            <InputDropDown type="search" title={"CONSULTATION"} values={consultationModeValues} typeable={false}
              onSelect={(value: any) => {
                if (value?.length > 0) {
                  let newFormData = formData;
                  newFormData.consultationMode = value[0].value;
                  setFormData((prevDetails) => ({ ...prevDetails, ...newFormData }));
                }
              }} />
            <InputDropDown type="search" title={"INVOICE"} values={invoiceStatusValues} typeable={false}
              defaultValue={formData.invoiceStatus}
              onSelect={(value: any) => {
                if (value?.length > 0) {
                  let newFormData = formData;
                  newFormData.invoiceStatus = value[0].value;
                  setFormData((prevDetails) => ({ ...prevDetails, ...newFormData }));
                }
              }} />
          </div>
          <div className="checkboxes">
            <div className="checkbox">
              <CheckBox icon={BlueHomeDotDisable} checkedIcon={BlueHomeDot} checked={formData.paperworkReceived}
                onChange={() => {
                  setFormData((prevFormData) => ({ ...prevFormData, paperworkReceived: !formData.paperworkReceived }));
                }} />
              Paperwork Received
            </div>
            <div className="checkbox">
              <CheckBox icon={BlueHomeDotDisable} checkedIcon={BlueHomeDot} checked={formData.appointmentStatus === 1}
                onChange={() => {
                  let newAppointmentStatus = formData.appointmentStatus === 0 ? 1 : 0;
                  setFormData((prevFormData) => ({ ...prevFormData, appointmentStatus: newAppointmentStatus }));
                }} />
              Appointment Confirmed
            </div>
          </div>
        </div>
        <div className="footer">
          <div className="submit-button">
            <DocumentButton text={"Submit"} onClick={() => saveAppointment()} loading={loading} />
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default AddAppointmentModal;
